import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { GRID_CONFIG } from '../../constants/tileConfig';

/**
 * Tile - A collapsible, resizable container for sidebar modules
 * Part of the grid-based layout system
 */
function Tile({
  id,
  icon,
  title,
  isCollapsed,
  onToggle,
  isPrimary = false,
  heightUnits = 2,
  minHeight = 1,
  maxHeight = 6,
  minHeightPx,
  children,
  collapsedContent,
  className,
  headerClassName,
  contentClassName,
}) {
  // Calculate pixel height based on units
  const collapsedHeight = GRID_CONFIG.heightUnit * minHeight;
  const isAutoHeight = heightUnits === 'auto';
  const expandedHeight = isPrimary || isAutoHeight ? 'auto' : GRID_CONFIG.heightUnit * heightUnits;
  
  // When collapsed with collapsedContent, use auto height instead of fixed collapsed height
  const hasCollapsedContent = isCollapsed && collapsedContent;

  return (
    <div
      className={cn(
        'tile',
        'flex flex-col',
        'bg-white rounded-lg shadow-sm border border-border',
        'transition-all duration-300 ease-in-out',
        // Only use overflow-hidden for fixed-height tiles without collapsed content
        !isAutoHeight && !isPrimary && !hasCollapsedContent && 'overflow-hidden',
        isCollapsed && !hasCollapsedContent && 'tile-collapsed',
        (isPrimary || isAutoHeight || hasCollapsedContent) && !isCollapsed && 'tile-auto-height',
        isPrimary && !isCollapsed && 'tile-primary flex-1',
        className
      )}
      style={{
        minHeight: isCollapsed 
          ? (hasCollapsedContent ? 'auto' : collapsedHeight) 
          : (minHeightPx ? `${minHeightPx}px` : (isPrimary ? '450px' : (isAutoHeight ? 'auto' : expandedHeight))),
        maxHeight: isPrimary && !isCollapsed
          ? 'none' 
          : (isCollapsed 
            ? (hasCollapsedContent ? 'none' : collapsedHeight) 
            : (isAutoHeight ? 'none' : GRID_CONFIG.heightUnit * maxHeight)),
      }}
      data-tile-id={id}
    >
      {/* Tile Header */}
      <div
        className={cn(
          'tile-header',
          'flex items-center justify-between',
          'px-3 py-2',
          'bg-gradient-to-r from-[#3c618a] to-[#7a67ba]',
          'text-white',
          'cursor-pointer select-none',
          'rounded-t-lg',
          'flex-shrink-0',
          headerClassName
        )}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-base">{icon}</span>}
          <span className="font-semibold text-sm">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-white hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Tile Content */}
      {(!isCollapsed || collapsedContent) && (
        <div
          className={cn(
            'tile-content',
            !isCollapsed && 'flex-1 min-h-0',
            'flex flex-col',
            'transition-all duration-300',
            'opacity-100 p-3',
            // Only add overflow-auto for fixed-height tiles, not auto-height
            !isAutoHeight && !isPrimary && 'overflow-auto',
            contentClassName
          )}
        >
          {isCollapsed ? collapsedContent : children}
        </div>
      )}
    </div>
  );
}

Tile.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isPrimary: PropTypes.bool,
  heightUnits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  minHeightPx: PropTypes.number,
  children: PropTypes.node,
  collapsedContent: PropTypes.node,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
};

export default Tile;
